(<template>
  <div :class="{'create-ent--full-width': fullForm}"
       class="create-ent">
    <div class="create-ent__avatar-label-wrapper">
      <label class="create-ent__avatar-label">
        <company-avatar :rotation="logoRotation"
                        :avatar="logoLink"
                        class="create-ent__avatar" />
        <span class="create-ent__avatar-label-text">{{ $gettext('Upload your company logo') }}</span>
        <input slot="fileInput"
               class="is-hidden"
               type="file"
               @change="catchAvatar">
      </label>
      <div v-if="logoErrors && logoErrors.length"
           class="create-ent__avatar-errors">
        <p v-for="(error, index) in logoErrors"
           :key="index">{{ error }}</p>
      </div>
      <button v-if="logoLink"
              class="create-ent__rotate-btn"
              @click="rotatePicture"></button>
    </div>
    <div class="create-ent__inputs-cont">
      <sk-select :type="'countries'"
                 :description="countryText"
                 :default-value="countryText"
                 :preselected-value="country"
                 :validation-name="countryErrorsName"
                 class="create-ent__input create-ent__country"
                 @csvaluechanged="setAddressCountry" />
      <autocomplete v-model="searchQuery"
                    class="create-ent__input create-ent__org-number"
                    :description="enterpriseText"
                    :placeholder="enterpriseText"
                    :suggestions="suggestions"
                    :validation-name="orgNumberErrorsName"
                    @search="searchEnterprise"
                    @suggestionselected="catchSuggestion" />
      <sk-input v-if="isNotNorwayCompany"
                :preselected-value="orgName"
                :description="orgNameText"
                :placeholder="orgNameText"
                :validation-name="orgNameErrorsName"
                :reg-exp="/<\/?[^>]+(>|$)/g"
                class="create-ent__input create-ent__org-name"
                @fieldvaluechanged="setOrgName" />
    </div>
    <div v-if="enterpriseExists"
         class="create-ent__warning ent-warning">
      <p>{{ alreadyExistsWarningText }}</p>
      <p class="ent-warning__text">{{ domainWhitelistText }}</p>
      <div class="ent-warning__block">
        <span v-html="actionWarningText"></span>
        <button v-if="whitelistExists"
                class="ent-warning__button"
                @click="redirectForSignUp">{{ $gettext('Create account') }}</button>
      </div>
    </div>
    <div v-if="isNotNorwayCompany"
         class="create-ent__org-note"
         v-html="notNorwayCompanyNote"></div>
    <terms-check :preselected-value="terms"
                 :validation-name="termsErrorsName"
                 class="create-ent__terms-checkbox"
                 @checkboxvaluechanged="setTerms" />
  </div>
</template>)

<script>
  import {mapState, mapMutations} from 'vuex';
  import Autocomplete from '@/components/shared_components/Autocomplete';

  export default {
    components: {
      autocomplete: Autocomplete,
    },
    props: {
      fullForm: Boolean
    },
    data() {
      return {
        searchQuery: '',
        suggestions: {},
        selectedSuggestion: ''
      };
    },
    computed: {
      ...mapState('TTWizardStore', {
        logoLink: (state) => state.stepsData.enterprise.logoLink,
        logoFile: (state) => state.stepsData.enterprise.logoFile,
        logoRotation: (state) => state.stepsData.enterprise.logoRotation,
        logoErrors: (state) => state.errors.avatar.errors,
        country: (state) => state.stepsData.address.country,
        countryErrorsName: (state) => state.errors.country.name,
        orgNumber: (state) => state.stepsData.enterprise.orgNumber,
        orgNumberErrorsName: (state) => state.errors.orgNumber.name,
        orgName: (state) => state.stepsData.enterprise.orgName,
        orgNameErrorsName: (state) => state.errors.orgName.name,
        terms: (state) => state.stepsData.enterprise.terms,
        termsErrorsName: (state) => state.errors.terms.name
      }),
      ...mapState('EnterpriseStore', {
        enterprises: (state) => state.enterprises || [],
        enterpriseExists: (state) => state.enterpriseExists,
        whitelistExists: (state) => state.whitelistExists,
        existingEnterpriseName: (state) => state.existingEnterpriseName
      }),
      countryText() { return this.$gettext('Country'); },
      enterpriseText() { return this.$gettext('Norwegian organisation number'); },
      orgNameText() { return this.$gettext('Company name'); },
      isNorway() { return this.country === 'Norway'; },
      isNotNorwayCompany() { return this.country && !this.isNorway; },
      notNorwayCompanyNote() {
        const template = this.$gettext('Please contact us by email at <a class="sk-link sk-link--default" href="mailto:%{support}">%{support}</a> so we can set up your account with an international payment method.');
        return this.$gettextInterpolate(template, {support: this.$getString('domainData', 'supportEmail')});
      },
      alreadyExistsWarningText() { return this.$gettext('The enterprise you would like to create already exists.'); },
      domainWhitelistText() {
        const whitelistTemplate = this.$gettext('%{orgName} has a domain whitelist that enables you to join their account directly, but you have to use your work email address to create an account.');
        const template = this.$gettext('%{orgName} does not have a domain whitelist, and you can only join by invitation from a colleague. Could you please ask your manager to invite you to the account?.');
        return this.$gettextInterpolate(this.whitelistExists ? whitelistTemplate : template, {orgName: this.existingEnterpriseName});
      },
      actionWarningText() {
        const template = this.$gettext('You can also contact us at <a href="mailto:%{email}" class="sk-link js-copy-to-clipboard">%{email}</a> or call us at %{phone} for assistance.');
        return this.whitelistExists
          ? this.$gettext('Would you like to try creating an account again with your work email?')
          : this.$gettextInterpolate(template, {email: this.$getString('domainData', 'supportEmail'), phone: this.$getString('domainData', 'supportPhone')});
      }
    },
    methods: {
      ...mapMutations('TTWizardStore', [
        'setError',
        'removeOneError',
        'setTerms',
        'setAddressCountry',
        'setOrgNumber',
        'setSubentitieFlag',
        'setOrgName',
        'setLogoFile',
        'setLogoLink',
        'setLogoRotation',
        'setPaymentCompany'
      ]),
      catchAvatar(eve) {
        let converterResponse = '';
        this.removeOneError('avatar');
        this.setLogoRotation(0);

        if (eve.target && eve.target.files[0]) {
          converterResponse = this.$avatarConverter.convertAvatar(this, eve.target.files[0]);
          if (!(converterResponse.then) && Array.isArray(converterResponse)) {
            for (const error of converterResponse) {
              this.setError({
                fieldName: 'avatar',
                errorText: error
              });
            }
          } else {
            converterResponse.then((response) => {
              this.setLogoLink(response.target.result);
              this.setLogoFile(eve.target.files[0]);
            });
          }
        }
      },
      rotatePicture() {
        this.setLogoRotation((this.logoRotation + 90) % 360);
      },
      catchSuggestion(value) {
        this.selectedSuggestion = value;
        this.setSubentitieFlag(value.subentitie);
        this.setOrgNumber(value.orgNumber);
      },
      searchEnterprise(term) {
        return this.$store.dispatch('EnterpriseStore/searchEnterprises', {query: term, search_brreg: true, search_subentity_brreg: true, search_platform: false, short_version: true}).then((response) => {
          const brreg = (response?.brregSearch || [])
            .slice(0, 3)
            .map((item) => ({...item, subentitie: false}));

          const subentities = (response?.brregSubentitySearch || [])
            .slice(0, 2)
            .map((item) => ({...item, subentitie: true}));

          this.suggestions = {
            brregSearch: [...brreg, ...subentities]
          };
        });
      },
      redirectForSignUp() {
        this.$store.dispatch('TTAuthStore/logOut').then(() => {
          this.$router.replace(this.$makeRoute({name: 'SignUp'}));
        });
      }
    }
  };
</script>

<style>
  .create-ent__country .sk-select__list {
    max-height: 165px;
  }
</style>

<style scoped>
  .create-ent {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .create-ent--full-width .create-ent__inputs-cont {
    margin-bottom: 20px;
    padding: 0 117px;
  }

  .create-ent--full-width .create-ent__org-note {
    padding: 0 117px;
  }

  .create-ent--full-width .create-ent__warning {
    margin: 0 117px;
  }

  .create-ent--full-width .create-ent__input {
    width: calc(50% - 10px);
  }

  .create-ent__warning {
    padding: 8px 8px 8px 32px;
    border-radius: 4px;
    background: url(~@assets/imgs/undefined_imgs/warning_icon_red.svg) no-repeat 8px 10px / 16px auto;
    background-color: var(--color-error-200);
    text-align: left;
  }

  .ent-warning__text {
    margin: 10px 0;
  }

  .ent-warning__block {
    display: flex;
    flex-wrap: wrap;
    gap: 0 4px;
  }

  .ent-warning__button {
    text-decoration: underline;
    white-space: nowrap;
  }

  .ent-warning__button:hover {
    text-decoration: none;
  }

  .create-ent__avatar-label-wrapper {
    position: relative;
    display: block;
    width: 200px;
    margin: 32px auto;
  }

  .create-ent__avatar-label {
    display: block;
    width: 100%;
    cursor: pointer;
  }

  .create-ent__avatar {
    width: 60px;
    height: 60px;
    margin-left: calc(50% - 30px);
  }

  .create-ent__avatar-label-text {
    display: block;
    width: 100%;
    margin-top: 16px;
    color: #000;
    text-align: center;
    text-decoration: underline;
  }

  .create-ent__avatar-label-text:hover {
    text-decoration: none;
  }

  .create-ent__rotate-btn {
    position: absolute;
    top: 35px;
    right: 35px;
    display: block;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    background-image: var(--image-rotate-button);
    background-position: 50% 50%;
    background-size: 100% auto;
    background-repeat: no-repeat;
  }

  .create-ent__avatar-errors {
    display: block;
    width: 100%;
    margin-top: 10px;
    padding: 0 25px;
    color: #f04;
    color: var(--color-error);
    font-size: 12px;
    text-align: left;
  }

  .create-ent__inputs-cont {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
  }

  .create-ent__input {
    width: 100%;
    margin-bottom: 15px;
  }

  .create-ent__terms-checkbox {
    width: auto;
    margin-top: 30px;
  }

  @media (max-width: 650px) {
    .create-ent__input {
      width: 100%;
    }

    .create-ent--full-width .create-ent__inputs-cont,
    .create-ent--full-width .create-ent__org-note {
      padding: 0 15px;
    }
  }
</style>
